import { AlertCircle, Calendar as CalendarIcon, RefreshCw } from 'lucide-react';
import React, { useState } from 'react';
import {
  CalendarGrid,
  CalendarToolbar,
  CalendarProjectFilter,
  CalendarEventTypeFilter,
  CalendarEventPopover,
} from '../components/calendar';
import { useCalendarFilters } from '../hooks/useCalendarFilters';
import { useGetCalendarEventsQuery } from '../services/calendarApi';
import { GlobalCalendarEvent } from '../types/calendar';

const Calendar: React.FC = () => {
  const {
    currentDate,
    view,
    selectedProjectIds,
    selectedEventTypes,
    filters,
    setView,
    setSelectedProjectIds,
    toggleEventType,
    navigateDate,
  } = useCalendarFilters();

  const {
    data: response,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useGetCalendarEventsQuery(filters);

  const [selectedEvent, setSelectedEvent] = useState<GlobalCalendarEvent | null>(null);
  const [popoverRect, setPopoverRect] = useState<DOMRect | null>(null);

  const events = response?.data?.events || [];
  const total = response?.data?.total || 0;

  const handleEventClick = (event: GlobalCalendarEvent) => {
    setSelectedEvent(event);
    // Use center of viewport as fallback anchor
    setPopoverRect(
      new DOMRect(window.innerWidth / 2 - 160, window.innerHeight / 2 - 120, 320, 240)
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <CalendarIcon className="h-6 w-6 text-gray-600" />
          <h1 className="text-xl font-semibold text-gray-900">Calendar</h1>
        </div>
      </div>

      {/* Calendar Container */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Toolbar */}
        <div className="border-b border-gray-200 p-4">
          <CalendarToolbar
            currentDate={currentDate}
            view={view}
            onViewChange={setView}
            onNavigate={navigateDate}
          />
        </div>

        {/* Filters */}
        <div className="border-b border-gray-200 px-4 py-3 space-y-2">
          <CalendarProjectFilter
            selectedProjectIds={selectedProjectIds}
            onChange={setSelectedProjectIds}
          />
          <CalendarEventTypeFilter selectedTypes={selectedEventTypes} onToggle={toggleEventType} />
        </div>

        {/* Content */}
        <div className="p-4 relative">
          {/* Fetching overlay (doesn't replace content) */}
          {isFetching && !isLoading && (
            <div className="absolute top-2 right-4 z-10 flex items-center space-x-2 bg-white/90 px-3 py-1.5 rounded-lg shadow-sm border border-gray-200">
              <RefreshCw className="h-3.5 w-3.5 text-brand-primary animate-spin" />
              <span className="text-xs text-gray-600">Updating...</span>
            </div>
          )}

          {/* Error state */}
          {isError ? (
            <div className="flex flex-col items-center justify-center py-16">
              <AlertCircle className="h-12 w-12 text-gray-300 mb-3" />
              <h3 className="text-base font-medium text-gray-900 mb-1">Failed to load events</h3>
              <p className="text-sm text-gray-500 mb-4">
                Something went wrong while fetching calendar events.
              </p>
              <button
                onClick={() => refetch()}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:bg-brand-700 transition-colors"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </button>
            </div>
          ) : !isLoading && events.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <CalendarIcon className="h-12 w-12 text-gray-300 mb-3" />
              <h3 className="text-base font-medium text-gray-900 mb-1">No events found</h3>
              <p className="text-sm text-gray-500">
                No events in this date range. Try adjusting your filters or navigating to a
                different period.
              </p>
            </div>
          ) : (
            <>
              {/* Large result set banner */}
              {total > events.length && (
                <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                  Showing first {events.length} of {total} events. Narrow your filters to see fewer
                  results.
                </div>
              )}

              <CalendarGrid
                view={view}
                date={currentDate}
                events={events}
                isLoading={isLoading}
                onEventClick={handleEventClick}
              />
            </>
          )}
        </div>
      </div>

      {/* Event detail popover */}
      <CalendarEventPopover
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        anchorRect={popoverRect}
      />
    </div>
  );
};

export default Calendar;
